import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, Clock, ArrowRight, Sparkles } from 'lucide-react';
import { useActiveCampaign } from '../../hooks/useDiscountCampaigns';
import { useUserPlan } from '../../hooks/useUserPlan';
import { useAuth } from '../../hooks/useAuth';

const STORAGE_KEY = 'discount_modal_seen';

function getDaysLeft(endDate: string) {
  const diff = new Date(endDate).getTime() - Date.now();
  return Math.max(0, Math.ceil(diff / 86400000));
}

function Countdown({ endDate }: { endDate: string }) {
  const [time, setTime] = useState({ h: 0, m: 0, s: 0 });

  useEffect(() => {
    const update = () => {
      const diff = new Date(endDate).getTime() - Date.now();
      if (diff <= 0) return;
      setTime({
        h: Math.floor(diff / 3600000) % 24,
        m: Math.floor((diff % 3600000) / 60000),
        s: Math.floor((diff % 60000) / 1000),
      });
    };
    update();
    const t = setInterval(update, 1000);
    return () => clearInterval(t);
  }, [endDate]);

  const pad = (n: number) => String(n).padStart(2, '0');

  return (
    <div className="flex items-center justify-center gap-2">
      {[{ v: time.h, l: 'h' }, { v: time.m, l: 'm' }, { v: time.s, l: 's' }].map(({ v, l }, i) => (
        <div key={l} className="flex items-center gap-2">
          <div className="flex flex-col items-center">
            <div className="w-14 h-14 bg-black/40 border border-white/10 rounded-xl flex items-center justify-center">
              <span className="text-2xl font-black text-white tabular-nums">{pad(v)}</span>
            </div>
            <span className="text-[10px] text-slate-400 mt-1 uppercase tracking-widest">{l}</span>
          </div>
          {i < 2 && <span className="text-white/40 text-2xl font-bold mb-4">:</span>}
        </div>
      ))}
    </div>
  );
}

export function DiscountPromoModal() {
  const { user } = useAuth();
  const { plan, loading: planLoading } = useUserPlan();
  const { campaign, loading: campaignLoading } = useActiveCampaign();
  const [visible, setVisible] = useState(false);
  const [animateIn, setAnimateIn] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    if (planLoading || campaignLoading) return;
    if (!user) return;
    if (plan !== 'free' && plan !== 'guest') return;
    if (!campaign) return;

    const seen = localStorage.getItem(STORAGE_KEY);
    if (seen === campaign.id) return;

    const t = setTimeout(() => {
      setVisible(true);
      setTimeout(() => setAnimateIn(true), 50);
    }, 1500);
    return () => clearTimeout(t);
  }, [user, plan, planLoading, campaign, campaignLoading]);

  const handleClose = () => {
    if (campaign) localStorage.setItem(STORAGE_KEY, campaign.id);
    setAnimateIn(false);
    setTimeout(() => setVisible(false), 300);
  };

  const handleCTA = () => {
    handleClose();
    setTimeout(() => navigate('/pricing'), 300);
  };

  if (!visible || !campaign) return null;

  const daysLeft = getDaysLeft(campaign.campaign_end);
  const plansLabel = campaign.plan_codes?.length
    ? campaign.plan_codes.map(c => c.charAt(0).toUpperCase() + c.slice(1)).join(' · ')
    : 'Todos los planes';

  return (
    <div
      className={`fixed inset-0 z-50 flex items-center justify-center p-4 transition-all duration-300 ${
        animateIn ? 'bg-black/70 backdrop-blur-md' : 'bg-black/0'
      }`}
      onClick={handleClose}
    >
      <div
        onClick={e => e.stopPropagation()}
        className={`relative w-full max-w-sm transition-all duration-300 ${
          animateIn ? 'opacity-100 scale-100 translate-y-0' : 'opacity-0 scale-90 translate-y-8'
        }`}
      >
        {/* Outer glow */}
        <div className="absolute -inset-1 bg-gradient-to-r from-emerald-500 via-blue-500 to-purple-500 rounded-3xl blur-lg opacity-60 animate-pulse" />

        {/* Card */}
        <div className="relative bg-[#080f1e] rounded-2xl overflow-hidden border border-white/10">

          {/* Top strip */}
          <div className="h-1.5 bg-gradient-to-r from-emerald-400 via-blue-400 to-purple-400" />

          {/* Close */}
          <button
            onClick={handleClose}
            className="absolute top-3 right-3 z-10 w-7 h-7 rounded-full bg-white/5 hover:bg-white/15 flex items-center justify-center text-slate-400 hover:text-white transition-all"
          >
            <X className="w-4 h-4" />
          </button>

          {/* Header bg pattern */}
          <div className="relative px-6 pt-6 pb-4 text-center overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-b from-emerald-500/10 to-transparent" />

            <div className="relative">
              {/* Badge */}
              <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-emerald-500/20 border border-emerald-500/40 text-emerald-400 text-xs font-bold mb-4 uppercase tracking-wider">
                <Sparkles className="w-3 h-3" />
                Oferta por tiempo limitado
              </div>

              {/* Big discount */}
              <div className="relative inline-block mb-2">
                <span className="text-8xl font-black bg-gradient-to-br from-emerald-300 via-white to-blue-300 bg-clip-text text-transparent leading-none">
                  {campaign.discount_percentage}%
                </span>
                <span
                  className="absolute -top-2 -right-8 text-2xl font-black text-emerald-400 rotate-12 drop-shadow-lg"
                  style={{ textShadow: '0 0 20px rgba(52,211,153,0.8)' }}
                >
                  OFF
                </span>
              </div>

              <p className="text-white font-bold text-lg">{campaign.name}</p>
              <p className="text-slate-400 text-sm mt-1">
                Durante <span className="text-white font-semibold">{campaign.discount_months} {campaign.discount_months === 1 ? 'mes' : 'meses'}</span>
                {' · '}
                <span className="text-blue-400">{plansLabel}</span>
              </p>
            </div>
          </div>

          {/* Countdown */}
          {daysLeft <= 7 && (
            <div className="px-6 pb-4">
              <div className="bg-black/40 border border-white/5 rounded-xl p-4">
                <div className="flex items-center justify-center gap-1.5 mb-3">
                  <Clock className="w-3.5 h-3.5 text-amber-400" />
                  <span className="text-amber-400 text-xs font-semibold uppercase tracking-wider">
                    La oferta termina en
                  </span>
                </div>
                <Countdown endDate={campaign.campaign_end} />
              </div>
            </div>
          )}

          {daysLeft > 7 && (
            <div className="px-6 pb-4">
              <div className="flex items-center justify-center gap-2 bg-amber-500/10 border border-amber-500/20 rounded-xl py-3">
                <Clock className="w-4 h-4 text-amber-400" />
                <span className="text-amber-300 text-sm font-medium">
                  Quedan <strong>{daysLeft} días</strong> para aprovechar
                </span>
              </div>
            </div>
          )}

          {/* CTA */}
          <div className="px-6 pb-6 space-y-2">
            <button
              onClick={handleCTA}
              className="group w-full relative py-4 rounded-xl font-black text-base overflow-hidden"
            >
              <div className="absolute inset-0 bg-gradient-to-r from-emerald-500 to-blue-500 transition-opacity group-hover:opacity-90" />
              <div className="absolute inset-0 bg-gradient-to-r from-emerald-400 to-blue-400 opacity-0 group-hover:opacity-100 transition-opacity" />
              <span className="relative flex items-center justify-center gap-2 text-white">
                Quiero mi descuento
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </span>
            </button>

            <button
              onClick={handleClose}
              className="w-full py-2 text-xs text-slate-600 hover:text-slate-400 transition-colors"
            >
              No me interesa por ahora
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
